/*
 * This file is part of glowingblue/orell-articles.
 *
 * Copyright (c) Glowing Blue AG.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import LiteraryWork from '../models/LiteraryWork';
import type Article from '../models/Article';
import shopRequestErrorHandler from '../utils/shopRequestErrorHandler';
import { ApiQueryParamsPlural } from 'flarum/common/Store';

export default class LiteraryWorkState {
	id: string = '';
	loading: boolean = false;
	literaryWork: LiteraryWork | null = null;

	constructor() {
		this.loading = true;
	}

	async refresh(): Promise<void> {
		this.literaryWork = null;

		const preloadedArticle = app.preloadedApiDocument<LiteraryWork>();
		if (preloadedArticle) {
			// This branch runs synchronously within the page's oninit (nothing is
			// awaited before it), so the first render already sees the data — no
			// redraw needed. `loading` must be cleared here, or the page keeps
			// showing the spinner forever on direct URL loads.
			this.literaryWork = preloadedArticle;
			this.loading = false;
		} else {
			// A search result we hold no row for yet: the id is the EAN, e.g.
			// `0000000000000-the-slug`.
			const matches = this.id.match(/([0-9]{13})/);
			if (matches) {
				const ean = matches[0];

				let article = app.store.createRecord<Article>('of_articles');

				try {
					article = await article.save({ ean }, { errorHandler: shopRequestErrorHandler });
				} catch (e) {
					// Shop no longer carries this EAN (or is unreachable), so there is no
					// work to load. Nothing else clears `loading` on this path — the
					// spinner would never stop.
					this.loading = false;
					m.redraw();

					return;
				}

				// The 3rd parameter tells Mithril to replace the current window.history
				// entry instead of inserting one, so the back button does not land on the
				// route where the ean was used.
				m.route.set(app.route.of_article(article), null, true);

				this.id = '' + article.literaryWorkId();
				this.load();
			} else {
				this.load();
			}
		}
	}

	load(): void {
		app.store
			.find<LiteraryWork>('of_literary_works', this.id, this.params())
			.then((literaryWork) => {
				this.literaryWork = literaryWork;
			})
			.catch((e) => console.error(e))
			.finally(() => {
				this.loading = false;
				m.redraw();
			});
	}

	params(): ApiQueryParamsPlural {
		return {
			include: 'articles,mainArticle.ownReview,mainArticle.ownReview.discussion',
		};
	}

	isLoading(): boolean {
		return this.loading;
	}

	getArticles(): Array<Article> {
		return (this.literaryWork?.articles() || []).filter((article): article is Article => article !== undefined);
	}
}
